import { useContext, useEffect, useRef, useState } from "react";
import {
  FiActivity,
  FiCheck,
  FiEdit2,
  FiHelpCircle,
  FiInfo,
  FiPlus,
  FiRefreshCw,
  FiShoppingBag,
  FiTrash2,
  FiTrendingUp,
  FiX,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";

import { api } from "../../api/api";
import { NavBar } from "../../components/NavBar";
import { GlobalContext } from "../../context/GlobalContext";
import { Toast } from "../../utils/Toast";
import "./style.css";

const EMPTY_FORM = {
  id: "",
  categoryId: "",
  title: "",
  description: "",
  active: false,
};

function responseList(response) {
  if (Array.isArray(response?.data)) return response.data;
  if (Array.isArray(response?.data?.data)) return response.data.data;
  return [];
}

export function CrossSellingPage() {
  const navigate = useNavigate();
  const { isAccess } = useContext(GlobalContext);
  const canUpdate = Boolean(isAccess("cardapio", "update"));

  const [crossSelling, setCrossSelling] = useState([]);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [productSelect, setProductSelect] = useState("");
  const [suggestions, setSuggestions] = useState(null);
  const [selectedSuggestions, setSelectedSuggestions] = useState({});
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [approvingCategory, setApprovingCategory] = useState("");
  const [editorOpen, setEditorOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState("");
  const [deletingId, setDeletingId] = useState("");
  const [crossSellingData, setCrossSellingData] = useState(EMPTY_FORM);
  const [crossSellingDataItems, setCrossSellingDataItems] = useState([]);
  const analysisRequestRef = useRef(0);
  const listRequestRef = useRef(0);
  const editorRequestRef = useRef(0);
  const modalRef = useRef(null);
  const helpModalRef = useRef(null);
  const previousFocusRef = useRef(null);
  const helpPreviousFocusRef = useRef(null);
  const savingRef = useRef(false);
  savingRef.current = saving;

  async function findAll() {
    const requestId = ++listRequestRef.current;
    const response = await api.get("/cross-selling");
    if (requestId === listRequestRef.current) setCrossSelling(responseList(response));
  }

  async function findAllCategory() {
    const response = await api.get("/category");
    setCategories(responseList(response));
  }

  async function findAllProducts() {
    const response = await api.get("/products");
    setProducts(responseList(response));
  }

  async function findSuggestions() {
    const requestId = ++analysisRequestRef.current;
    try {
      setLoadingSuggestions(true);
      const response = await api.get("/cross-selling-automation/suggestions");
      if (requestId !== analysisRequestRef.current) return;
      const data = {
        ...response.data,
        categories: Array.isArray(response.data?.categories)
          ? response.data.categories
              .map((category) => ({
                ...category,
                candidates: Array.isArray(category?.candidates)
                  ? category.candidates.filter((candidate) => !candidate.configuredActive)
                  : [],
              }))
              .filter((category) => category.candidates.length > 0)
          : [],
      };
      setSuggestions(data);

      const initialSelection = {};
      data.categories.forEach((category) => {
        initialSelection[category.categoryId] = category.candidates
          .filter((candidate) => !candidate.alreadyConfigured || !candidate.configuredActive)
          .slice(0, 3)
          .map((candidate) => candidate.productId);
      });
      setSelectedSuggestions(initialSelection);
    } catch (error) {
      if (requestId === analysisRequestRef.current) {
        Toast.warning(error?.response?.data?.message || "Não foi possível calcular as sugestões.");
      }
    } finally {
      if (requestId === analysisRequestRef.current) setLoadingSuggestions(false);
    }
  }

  function toggleSuggestion(categoryId, productId) {
    const selected = selectedSuggestions[categoryId] || [];
    if (!selected.includes(productId) && selected.length >= 5) {
      return Toast.warning("Selecione no máximo cinco produtos.");
    }
    setSelectedSuggestions((current) => {
      const currentSelection = current[categoryId] || [];
      return {
        ...current,
        [categoryId]: currentSelection.includes(productId)
          ? currentSelection.filter((id) => id !== productId)
          : [...currentSelection, productId],
      };
    });
  }

  async function approveSuggestions(category, active) {
    if (!canUpdate) return Toast.warning("Você não tem permissão para alterar a venda cruzada.");
    const productIds = selectedSuggestions[category.categoryId] || [];
    if (productIds.length === 0) return Toast.warning("Selecione pelo menos um produto.");

    const confirm = await Toast.confirm(
      active ? "Deseja aprovar e ativar estas sugestões?" : "Deseja salvar estas sugestões como rascunho?",
      "Cancelar",
      active ? "Aprovar e ativar" : "Salvar rascunho"
    );
    if (!confirm) return;

    try {
      setApprovingCategory(category.categoryId);
      await api.post("/cross-selling-automation/approve", { categoryId: category.categoryId, productIds, active });
      Toast.success(active ? "Sugestões ativadas com sucesso." : "Sugestões salvas como rascunho.");
    } catch (error) {
      Toast.warning(error?.response?.data?.message || "Não foi possível aprovar as sugestões.");
      setApprovingCategory("");
      return;
    }

    const refreshResults = await Promise.allSettled([findAll(), findSuggestions()]);
    setApprovingCategory("");
    if (refreshResults.some((result) => result.status === "rejected")) {
      Toast.warning("A aprovação foi concluída, mas não foi possível atualizar toda a tela.");
    }
  }

  async function create() {
    if (!canUpdate) return Toast.warning("Você não tem permissão para alterar a venda cruzada.");
    if (saving) return;
    try {
      setSaving(true);
      await api.post("/cross-selling", { ...crossSellingData, products: crossSellingDataItems });
      dismissEditor();
      clearForm();
      Toast.success("Registro salvo com sucesso.");
    } catch (error) {
      Toast.warning(error?.response?.data?.message || "Não foi possível salvar a venda cruzada.");
      return;
    } finally {
      setSaving(false);
    }
    try {
      await findAll();
    } catch (error) {
      Toast.warning("O registro foi salvo, mas a lista não pôde ser atualizada.");
    }
  }

  async function update(id) {
    if (!canUpdate) return Toast.warning("Você não tem permissão para alterar a venda cruzada.");
    if (saving) return;
    try {
      setSaving(true);
      await api.put(`/cross-selling/${id}`, { ...crossSellingData, products: crossSellingDataItems });
      dismissEditor();
      clearForm();
      Toast.success("Registro atualizado com sucesso.");
    } catch (error) {
      Toast.warning(error?.response?.data?.message || "Não foi possível atualizar a venda cruzada.");
      return;
    } finally {
      setSaving(false);
    }
    try {
      await findAll();
    } catch (error) {
      Toast.warning("O registro foi atualizado, mas a lista não pôde ser recarregada.");
    }
  }

  async function destroy(id) {
    if (!canUpdate) return Toast.warning("Você não tem permissão para alterar a venda cruzada.");
    if (deletingId) return;
    const confirm = await Toast.confirm();
    if (!confirm) return;
    try {
      setDeletingId(id);
      await api.delete(`/cross-selling/${id}`);
      Toast.success("Registro excluído com sucesso.");
    } catch (error) {
      Toast.warning(error?.response?.data?.message || "Não foi possível excluir a venda cruzada.");
      return;
    } finally {
      setDeletingId("");
    }
    try {
      await findAll();
    } catch (error) {
      Toast.warning("O registro foi excluído, mas a lista não pôde ser recarregada.");
    }
  }

  async function edit(id) {
    const requestId = ++editorRequestRef.current;
    previousFocusRef.current = document.activeElement;
    try {
      setEditingId(id);
      const response = await api.get(`/cross-selling/${id}`);
      if (requestId !== editorRequestRef.current) return;
      setCrossSellingData({
        id: response.data.id,
        categoryId: String(response.data.categoryId || ""),
        title: response.data.title || "",
        description: response.data.description || "",
        active: Boolean(response.data.active),
      });
      setCrossSellingDataItems(Array.isArray(response.data.products) ? response.data.products : []);
      setProductSelect("");
      setEditorOpen(true);
    } catch (error) {
      if (requestId === editorRequestRef.current) {
        Toast.warning(error?.response?.data?.message || "Não foi possível carregar a venda cruzada.");
      }
    } finally {
      if (requestId === editorRequestRef.current) setEditingId("");
    }
  }

  function clearForm() {
    setCrossSellingData(EMPTY_FORM);
    setCrossSellingDataItems([]);
    setProductSelect("");
  }

  function openNewConfiguration() {
    editorRequestRef.current += 1;
    clearForm();
    previousFocusRef.current = document.activeElement;
    setEditorOpen(true);
  }

  function closeEditor() {
    if (saving) return;
    dismissEditor();
  }

  function dismissEditor() {
    editorRequestRef.current += 1;
    setEditorOpen(false);
    window.setTimeout(() => previousFocusRef.current?.focus(), 0);
  }

  function openHelp() {
    helpPreviousFocusRef.current = document.activeElement;
    setHelpOpen(true);
  }

  function closeHelp() {
    setHelpOpen(false);
    window.setTimeout(() => helpPreviousFocusRef.current?.focus(), 0);
  }

  function changeCategory(categoryId) {
    const normalizedCategoryId = String(categoryId || "");
    setCrossSellingData({ ...crossSellingData, categoryId: normalizedCategoryId });
    setProductSelect("");
    setCrossSellingDataItems((current) => current.filter((item) => {
      const product = products.find((value) => String(value.id) === String(item.productId));
      return !product || String(product.categoryId) !== normalizedCategoryId;
    }));
  }

  function addProduct() {
    if (!productSelect) return Toast.warning("Selecione um produto.");
    if (crossSellingDataItems.length >= 5) return Toast.warning("Adicione no máximo cinco produtos.");
    if (crossSellingDataItems.some((item) => String(item.productId) === String(productSelect))) {
      return Toast.warning("Esse produto já foi adicionado.");
    }

    const product = products.find((item) => String(item.id) === String(productSelect));
    if (!product) return Toast.warning("Produto não encontrado.");
    setCrossSellingDataItems([...crossSellingDataItems, { productId: product.id, name: product.name }]);
    setProductSelect("");
  }

  async function removeProduct(id, productId) {
    const confirm = await Toast.confirm("Deseja remover o produto?", "Não", "Sim");
    if (confirm) {
      setCrossSellingDataItems((current) => current.filter((item) => (id ? item.id !== id : item.productId !== productId)));
    }
  }

  function emptyAnalysisMessage() {
    const diagnostics = suggestions?.diagnostics;
    const criteria = suggestions?.criteria;
    const total = suggestions?.window?.eligibleOrders || 0;

    if (suggestions?.insufficientSample) {
      return `Foram encontrados ${total} pedidos online. São necessários pelo menos ${suggestions.window.minimumEligibleOrders} para iniciar a análise.`;
    }
    if (!diagnostics?.multiProductOrders) {
      return `Os ${total} pedidos analisados possuem apenas um produto por pedido, por isso ainda não há combinações para comparar.`;
    }
    if (!diagnostics?.crossCategoryOrders) {
      return `Dos ${total} pedidos, ${diagnostics.multiProductOrders} tinham mais de um produto, mas nenhum combinou categorias diferentes.`;
    }
    if ((diagnostics?.qualifyingPairs || 0) > 0 && (diagnostics?.actionablePairs || 0) === 0) {
      return "Todas as relações qualificadas encontradas já estão ativas nas configurações de venda cruzada.";
    }
    if ((diagnostics.strongestPairOrders || 0) < (criteria?.minimumPairOrders || 3)) {
      return `A melhor combinação apareceu ${diagnostics.strongestPairOrders || 0} vez(es). O mínimo para sugerir uma relação é ${criteria?.minimumPairOrders || 3}.`;
    }
    return `${diagnostics.crossCategoryOrders} pedidos combinaram categorias diferentes, mas os pares ainda não atingiram confiança suficiente.`;
  }

  useEffect(() => {
    loadInitialData();
    findSuggestions();
    // Initial page load only; refreshes are triggered explicitly after mutations.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadInitialData() {
    try {
      setLoadingData(true);
      setLoadError("");
      await Promise.all([findAll(), findAllCategory(), findAllProducts()]);
    } catch (error) {
      setLoadError(error?.response?.data?.message || "Não foi possível carregar categorias, produtos e configurações.");
    } finally {
      setLoadingData(false);
    }
  }

  useEffect(() => {
    if (!editorOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.setTimeout(() => modalRef.current?.querySelector("button")?.focus(), 0);

    function handleKeyDown(event) {
      if (event.key === "Escape" && !savingRef.current) {
        editorRequestRef.current += 1;
        setEditorOpen(false);
        window.setTimeout(() => previousFocusRef.current?.focus(), 0);
      }
      if (event.key !== "Tab") return;
      const focusable = Array.from(modalRef.current?.querySelectorAll("button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled])") || []);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [editorOpen]);

  useEffect(() => {
    if (!helpOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.setTimeout(() => helpModalRef.current?.querySelector("button")?.focus(), 0);

    function handleKeyDown(event) {
      if (event.key === "Escape") closeHelp();
      if (event.key !== "Tab") return;
      const focusable = Array.from(helpModalRef.current?.querySelectorAll("button:not([disabled])") || []);
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);
    };
    // Modal lifecycle is controlled only by helpOpen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [helpOpen]);

  const diagnostics = suggestions?.diagnostics;
  const availableProducts = products.filter((product) => String(product.categoryId) !== String(crossSellingData.categoryId));
  const mutationInProgress = saving || Boolean(deletingId) || Boolean(approvingCategory);

  return (
    <NavBar>
      <main className="cross-selling-page">
        <header className="cross-selling-page-header">
          <div>
            <h1>Venda cruzada</h1>
            <p>Configure ofertas complementares e descubra produtos comprados juntos no cardápio online</p>
          </div>
          <button type="button" className="cross-selling-help-button" onClick={openHelp}>
            <FiHelpCircle size={17} /> Ajuda
          </button>
        </header>

        {!loadingData && loadError && (
          <div className="cross-selling-notice error">
            <FiInfo size={18} />
            <span>{loadError}</span>
            <button type="button" onClick={loadInitialData}>Tentar novamente</button>
          </div>
        )}

        {!loadingData && !loadError && (categories.length === 0 || products.length === 0) && (
          <div className="cross-selling-notice">
            <FiInfo size={18} />
            <span>Cadastre ao menos uma categoria e um produto para configurar vendas cruzadas.</span>
            <button type="button" onClick={() => navigate("/menu")}>Acessar cardápio</button>
          </div>
        )}

        <section className="cross-selling-analysis" aria-busy={loadingSuggestions} aria-live="polite">
          <div className="cross-selling-section-heading">
            <span><FiTrendingUp size={19} /></span>
            <div>
              <h2>Sugestões automáticas</h2>
              <p>Relações encontradas no histórico de delivery e retirada do cardápio</p>
            </div>
            <button type="button" className="cross-selling-refresh" disabled={loadingSuggestions || mutationInProgress} onClick={findSuggestions}>
              <FiRefreshCw className={loadingSuggestions ? "spinning" : ""} size={16} />
              {loadingSuggestions ? "Analisando" : "Atualizar análise"}
            </button>
          </div>

          {suggestions?.window && (
            <div className="cross-selling-stats">
              <article><span><FiShoppingBag /></span><div><strong>{suggestions.window.eligibleOrders}</strong><small>pedidos analisados</small></div></article>
              <article><span><FiActivity /></span><div><strong>{diagnostics?.multiProductOrders || 0}</strong><small>com mais de um produto</small></div></article>
              <article><span><FiTrendingUp /></span><div><strong>{diagnostics?.crossCategoryOrders || 0}</strong><small>com categorias diferentes</small></div></article>
              <article><span><FiCheck /></span><div><strong>{diagnostics?.actionablePairs ?? diagnostics?.qualifyingPairs ?? 0}</strong><small>sugestões disponíveis</small></div></article>
              <article className="period"><strong>{suggestions.window.effectiveDays} dias</strong><small>janela analisada</small></article>
            </div>
          )}

          {suggestions?.window && (
            <div className="cross-selling-metric-legend">
              <div>
                <strong>Como interpretar os números</strong>
                <span>As métricas usam pedidos distintos; a quantidade do mesmo produto não aumenta a contagem.</span>
              </div>
              <dl>
                <div><dt>Pedidos juntos</dt><dd>Quantos pedidos continham a categoria e o produto.</dd></div>
                <div><dt>Confiança</dt><dd>Percentual dos pedidos da categoria que também tinham o produto.</dd></div>
                <div><dt>Afinidade</dt><dd>Quanto o produto fica mais provável em relação à média.</dd></div>
              </dl>
              <button type="button" onClick={openHelp}><FiHelpCircle size={16} /> Entenda os cálculos</button>
            </div>
          )}

          {!loadingSuggestions && suggestions && suggestions.categories.length === 0 && (
            <div className="cross-selling-analysis-empty">
              <span><FiTrendingUp size={25} /></span>
              <h3>Nenhuma relação disponível ainda</h3>
              <p>{emptyAnalysisMessage()}</p>
            </div>
          )}

          <div className="cross-selling-suggestion-grid">
            {suggestions?.categories?.map((category) => (
              <article className="cross-selling-suggestion-card" key={category.categoryId}>
                <header>
                  <div><h3>{category.categoryName}</h3><p>Categoria presente em {category.sourceOrders} pedidos</p></div>
                  <span>{category.candidates.length} sugestão(ões)</span>
                </header>
                <div className="cross-selling-candidates">
                  {category.candidates.map((candidate) => (
                    <label className={candidate.alreadyConfigured && candidate.configuredActive ? "configured" : ""} key={candidate.productId}>
                      <input
                        type="checkbox"
                        checked={(selectedSuggestions[category.categoryId] || []).includes(candidate.productId)}
                        disabled={loadingSuggestions || mutationInProgress || (candidate.alreadyConfigured && candidate.configuredActive)}
                        onChange={() => toggleSuggestion(category.categoryId, candidate.productId)}
                      />
                      <span className="candidate-check"><FiCheck size={13} /></span>
                      <span className="candidate-content">
                        <strong>{candidate.productName}</strong>
                        <small>{candidate.productCategoryName}</small>
                        <span className="candidate-metrics">
                          <b>{Number(candidate.pairOrders || 0)} pedidos juntos</b>
                          <b>{(Number(candidate.confidence || 0) * 100).toFixed(0)}% de confiança</b>
                          <b>{Number(candidate.lift || 0).toFixed(2)}x de afinidade</b>
                        </span>
                      </span>
                      <span className={`candidate-status ${candidate.alreadyConfigured ? "draft" : candidate.strength}`}>
                        {candidate.alreadyConfigured ? "Rascunho" : candidate.strength === "strong" ? "Forte" : "Promissora"}
                      </span>
                    </label>
                  ))}
                </div>
                <footer>
                  <button type="button" className="secondary" disabled={!canUpdate || loadingSuggestions || mutationInProgress} onClick={() => approveSuggestions(category, false)}>Salvar rascunho</button>
                  <button type="button" disabled={!canUpdate || loadingSuggestions || mutationInProgress} onClick={() => approveSuggestions(category, true)}><FiCheck size={15} /> Aprovar e ativar</button>
                </footer>
              </article>
            ))}
          </div>
        </section>

        <section className="cross-selling-configurations">
          <div className="cross-selling-section-heading">
            <span><FiShoppingBag size={19} /></span>
            <div><h2>Configurações</h2><p>{loadingData ? "Carregando configurações" : `${crossSelling.length} ${crossSelling.length === 1 ? "venda cruzada cadastrada" : "vendas cruzadas cadastradas"}`}</p></div>
            <button type="button" className="cross-selling-primary-button" disabled={!canUpdate || loadingData || Boolean(editingId) || mutationInProgress || categories.length === 0 || products.length === 0} onClick={openNewConfiguration}><FiPlus size={16} /> Nova configuração</button>
          </div>

          <div className="cross-selling-table-wrap">
            <table className="cross-selling-table">
              <thead><tr><th scope="col">Categoria</th><th scope="col">Título</th><th scope="col">Status</th><th scope="col">Ações</th></tr></thead>
              <tbody>
                {crossSelling.map((item) => (
                  <tr key={item.id}>
                    <td data-label="Categoria"><strong>{item.name}</strong></td>
                    <td data-label="Título">{item.title || "Sem título"}</td>
                    <td data-label="Status"><span className={`configuration-status ${item.active ? "active" : "draft"}`}>{item.active ? "Ativa" : "Rascunho"}</span></td>
                    <td data-label="Ações" className="configuration-actions">
                      <button type="button" aria-label="Editar" disabled={Boolean(editingId) || mutationInProgress} onClick={() => edit(item.id)}><FiEdit2 size={16} /></button>
                      <button type="button" aria-label="Excluir" className="danger" disabled={!canUpdate || Boolean(editingId) || mutationInProgress} onClick={() => destroy(item.id)}><FiTrash2 size={16} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {loadingData && <div className="cross-selling-table-empty"><strong>Carregando configurações...</strong></div>}
            {!loadingData && loadError && <div className="cross-selling-table-empty"><strong>Não foi possível carregar as configurações.</strong><span>Use “Tentar novamente” para repetir a consulta.</span></div>}
            {!loadingData && !loadError && crossSelling.length === 0 && (
              <div className="cross-selling-table-empty"><FiShoppingBag size={25} /><strong>Nenhuma configuração cadastrada</strong><span>Crie uma configuração manual ou aprove uma sugestão automática.</span></div>
            )}
          </div>
        </section>

        {editorOpen && (
          <div className="cross-selling-modal-backdrop" onMouseDown={closeEditor}>
            <section ref={modalRef} className="cross-selling-modal" role="dialog" aria-modal="true" aria-labelledby="cross-selling-modal-title" onMouseDown={(event) => event.stopPropagation()}>
              <header>
                <span><FiShoppingBag size={19} /></span>
                <div><h2 id="cross-selling-modal-title">{crossSellingData.id ? "Editar venda cruzada" : "Nova venda cruzada"}</h2><p>Escolha uma categoria de origem e até cinco produtos complementares</p></div>
                <button type="button" aria-label="Fechar" disabled={saving} onClick={closeEditor}><FiX size={20} /></button>
              </header>

              <fieldset className="cross-selling-form" disabled={saving}>
                <label className="cross-selling-active-field">
                  <span><strong>Configuração ativa</strong><small>Exibir estas sugestões no cardápio</small></span>
                  <span className="cross-selling-switch"><input type="checkbox" checked={crossSellingData.active} onChange={(event) => setCrossSellingData({ ...crossSellingData, active: event.target.checked })} /><span /></span>
                </label>

                <div className="cross-selling-form-grid">
                  <label><span>Categoria de origem</span><select value={crossSellingData.categoryId} onChange={(event) => changeCategory(event.target.value)}><option value="">Selecione a categoria</option>{categories.map((category) => <option key={category.id} value={String(category.id)}>{category.name}</option>)}</select></label>
                  <label><span>Título</span><input type="text" maxLength={30} placeholder="Ex.: Complete seu pedido" value={crossSellingData.title} onChange={(event) => setCrossSellingData({ ...crossSellingData, title: event.target.value })} /></label>
                  <label className="full"><span>Descrição</span><textarea rows="3" maxLength={100} placeholder="Texto exibido junto às sugestões" value={crossSellingData.description} onChange={(event) => setCrossSellingData({ ...crossSellingData, description: event.target.value })} /></label>
                </div>

                <div className="cross-selling-product-field">
                  <div><strong>Produtos sugeridos</strong><small>{crossSellingDataItems.length}/5 selecionados</small></div>
                  <div className="cross-selling-product-picker">
                    <select value={productSelect} onChange={(event) => setProductSelect(event.target.value)}><option value="">Selecione um produto</option>{availableProducts.map((product) => <option key={product.id} value={product.id}>{product.name}</option>)}</select>
                    <button type="button" disabled={!productSelect || crossSellingDataItems.length >= 5} onClick={addProduct}><FiPlus size={16} /> Adicionar</button>
                  </div>
                  <div className="cross-selling-selected-products">
                    {crossSellingDataItems.map((item) => <span key={item.id ? `association-${item.id}` : `product-${item.productId}`}>{item.name}<button type="button" disabled={saving} aria-label={`Remover ${item.name}`} onClick={() => removeProduct(item.id, item.productId)}><FiX size={14} /></button></span>)}
                    {crossSellingDataItems.length === 0 && <p>Nenhum produto selecionado.</p>}
                  </div>
                </div>
              </fieldset>

              <footer>
                <button type="button" className="secondary" disabled={saving} onClick={closeEditor}>Cancelar</button>
                <button type="button" disabled={saving || !canUpdate || !crossSellingData.categoryId || crossSellingDataItems.length === 0} onClick={() => crossSellingData.id ? update(crossSellingData.id) : create()}><FiCheck size={16} /> {saving ? "Salvando..." : "Salvar configuração"}</button>
              </footer>
            </section>
          </div>
        )}

        {helpOpen && (
          <div className="cross-selling-modal-backdrop" onMouseDown={closeHelp}>
            <section ref={helpModalRef} className="cross-selling-modal cross-selling-help-modal" role="dialog" aria-modal="true" aria-labelledby="cross-selling-help-title" onMouseDown={(event) => event.stopPropagation()}>
              <header>
                <span><FiHelpCircle size={20} /></span>
                <div><h2 id="cross-selling-help-title">Como funcionam as sugestões</h2><p>Entenda a base analisada, os cálculos e a classificação das relações</p></div>
                <button type="button" aria-label="Fechar ajuda" onClick={closeHelp}><FiX size={20} /></button>
              </header>

              <div className="cross-selling-help-content">
                <section>
                  <h3>Quais pedidos entram na análise?</h3>
                  <p>São considerados pedidos finalizados de delivery e retirada feitos pelo cardápio online. A análise começa com 90 dias e amplia para 180 dias quando encontra menos de 100 pedidos.</p>
                  <p>Produtos antigos ajudam a compor o histórico, mas somente produtos e categorias atualmente ativos podem ser sugeridos.</p>
                </section>

                <section>
                  <h3>Como os números são calculados?</h3>
                  <div className="cross-selling-formulas">
                    <article>
                      <strong>Pedidos juntos</strong>
                      <p>Número de pedidos distintos que continham ao menos um produto da categoria de origem e o produto sugerido.</p>
                      <code>pedidos com categoria + produto</code>
                    </article>
                    <article>
                      <strong>Confiança</strong>
                      <p>Mostra em quantos pedidos da categoria o produto sugerido também apareceu.</p>
                      <code>pedidos juntos ÷ pedidos da categoria × 100</code>
                    </article>
                    <article>
                      <strong>Afinidade</strong>
                      <p>Compara a chance do produto aparecer com a categoria contra a chance dele aparecer em qualquer pedido.</p>
                      <code>confiança ÷ frequência geral do produto</code>
                    </article>
                  </div>
                  <div className="cross-selling-example">
                    <FiInfo size={18} />
                    <div><strong>Exemplo</strong><p>Se uma categoria esteve em 454 pedidos e o produto apareceu junto em 103, a confiança é <b>103 ÷ 454 = 22,7%</b>. Afinidade de <b>1,12x</b> significa que esse produto fica aproximadamente 12% mais provável quando a categoria está no pedido.</p></div>
                  </div>
                </section>

                <section>
                  <h3>Forte ou promissora?</h3>
                  <div className="cross-selling-strengths">
                    <article><span className="candidate-status strong">Forte</span><p>Categoria em pelo menos 20 pedidos, produto em 5, par em 5, confiança mínima de 10% e afinidade de 1,20x.</p></article>
                    <article><span className="candidate-status promising">Promissora</span><p>Categoria em pelo menos {suggestions?.criteria?.minimumSourceOrders || 5} pedidos, par em {suggestions?.criteria?.minimumPairOrders || 3}, confiança de {((suggestions?.criteria?.minimumConfidence || 0.08) * 100).toFixed(0)}% e afinidade mínima de {(suggestions?.criteria?.minimumLift || 1).toFixed(2)}x.</p></article>
                  </div>
                  <p className="cross-selling-help-note">A classificação ajuda na decisão, mas nenhuma relação é ativada automaticamente. Você sempre escolhe o que será publicado.</p>
                </section>
              </div>

              <footer><button type="button" onClick={closeHelp}><FiCheck size={16} /> Entendi</button></footer>
            </section>
          </div>
        )}
      </main>
    </NavBar>
  );
}
